/// <amd-module name="Wit/WorkItemForm/Common/Components/PartitionedList" />
import * as React from "react";
import { useEffect, useState } from "react";
import { Icon } from "VSSUI/Icon";
import { List, IListProps, IListItemDetails, ListItem, IListItemProps, IListRow } from "VSSUI/List";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { useSet } from "../Hooks";

export interface IPartition {
    key: string;
    label: string;
    order: number;
}

/**
 * We want a class here so that we can check instanceof at runtime.
 */
class DividerPlaceholder {
    constructor(public key: string, public label: string) {}
}

export interface IPartitionedListProps<T extends object> extends Omit<IListProps<T>, "itemProvider"> {
    items: T[];
    /**
     * A function which takes a list item, and returns an object that indicates
     * which section of the list the item should belong to. The key properties
     * on these objects must be unique; duplicate keys will be considered the
     * same partition. Return values with the same key should also have the same
     * label and order.
     */
    getPartition: (item: T) => IPartition;
}

interface IItemMetadata {
    collapsed: boolean;
    partitionKey: string;
}

export function PartitionedList<T extends object>({ items, getPartition, renderRow, ...props }: IPartitionedListProps<T>) {
    const [itemProvider, setItemProvider] = useState(new ArrayItemProvider<(T & IItemMetadata) | DividerPlaceholder>([]));
    const [isCollapsed, collapse, expand] = useSet<string>();

    // Update the itemProvider for the list whenever:
    // - the list of items changes
    // - the partition function changes
    // - the collapsed state changes
    useEffect(() => {
        const itemsMap = new Map<string, T[]>();
        const partitionsMap = new Map<string, IPartition>();

        for (const item of items) {
            const partition = getPartition(item);
            if (!partitionsMap.has(partition.key)) {
                partitionsMap.set(partition.key, partition);
            }

            if (!itemsMap.has(partition.key)) {
                itemsMap.set(partition.key, []);
            }
            itemsMap.get(partition.key)!.push(item);
        }

        const orderedPartitions = [...partitionsMap.values()].sort((a, b) => a.order - b.order);

        let itemsWithPlaceholders: ((T & IItemMetadata) | DividerPlaceholder)[] = [];
        for (const partition of orderedPartitions) {
            const placeholder = new DividerPlaceholder(partition.key, partition.label);
            const itemsInPartition = itemsMap
                .get(partition.key)!
                .map(item => ({ ...item, collapsed: isCollapsed(partition.key), partitionKey: partition.key }));

            itemsWithPlaceholders = [...itemsWithPlaceholders, placeholder, ...itemsInPartition];
        }

        setItemProvider(new ArrayItemProvider<(T & IItemMetadata) | DividerPlaceholder>(itemsWithPlaceholders));
    }, [items, getPartition, isCollapsed]);

    const renderRowOrDivider: IListProps<(T & IItemMetadata) | DividerPlaceholder>["renderRow"] = (index, item, details) => {
        if (item instanceof DividerPlaceholder) {
            return (
                <PartitionDivider
                    key={`partition-divider-${item.key}`}
                    listItemProps={{ index, details: details as IListItemDetails<DividerPlaceholder> }}
                    label={item.label}
                    expanded={!isCollapsed(item.key)}
                    onToggle={() => {
                        if (isCollapsed(item.key)) {
                            expand(item.key);
                        } else {
                            collapse(item.key);
                        }
                    }}
                />
            );
        }

        // item is a `T & ItemMetadata`
        if (item.collapsed) {
            // Do not render the row if the partition it's in is collapsed
            return null;
        }

        return renderRow(index, item, details as IListItemDetails<T>);
    };

    return (
        <List
            {...(props as IListProps<(T & IItemMetadata) | DividerPlaceholder>)}
            itemProvider={itemProvider}
            renderRow={renderRowOrDivider}
            onFocus={(event, listRow) => {
                const item = listRow.data;
                if (item instanceof DividerPlaceholder) {
                    // Do nothing
                    return;
                }

                // Delegate to the consumer
                props.onFocus?.(event, listRow as IListRow<T>);
            }}
            onSelect={(event, listRow) => {
                const item = listRow.data;
                if (item instanceof DividerPlaceholder) {
                    // Do nothing
                    return;
                }

                // Delegate to the consumer
                props.onSelect?.(event, listRow as IListRow<T>);
            }}
            onActivate={(event, listRow) => {
                const item = listRow.data;
                if (item instanceof DividerPlaceholder) {
                    if (isCollapsed(item.key)) {
                        expand(item.key);
                    } else {
                        collapse(item.key);
                    }
                    return;
                }

                // Delegate to the consumer
                props.onActivate?.(event, listRow as IListRow<T>);
            }}
        />
    );
}

interface IPartitionDividerProps {
    label: string;
    expanded: boolean;
    onToggle: () => void;
    listItemProps: IListItemProps<DividerPlaceholder>;
}

function PartitionDivider({ label, expanded, onToggle, listItemProps }: IPartitionDividerProps) {
    return (
        <ListItem {...listItemProps}>
            <div className="partitioned-list-divider padding-8" onClick={onToggle}>
                <Icon className="icon-margin" iconName={expanded ? "ChevronDown" : "ChevronRight"} />
                {label}
            </div>
        </ListItem>
    );
}
